// DashboardPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
    Container, Typography, Box, Grid, Card, CardContent, CardActions,
    Button, Fab, MenuItem, Chip, IconButton, Menu, ListItemIcon, ListItemText,
    Popover, InputBase, Divider, Snackbar, Alert, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import {
    Add as AddIcon, MoreVert as MoreVertIcon, Share as ShareIcon,
    Download as DownloadIcon, Edit as EditIcon, Delete as DeleteIcon,
    Visibility as VisibilityIcon, ContentCopy as ContentCopyIcon,
    Email as EmailIcon, CheckCircleOutline as CheckIcon,
    QrCode as QrCodeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { CVVersion } from '../types';
import AdBanner from '../components/AdBanner';
import { useTranslation } from 'react-i18next';
import { CvVersionAPI } from '../services/API/CvVersionAPI';
import { Utility } from '../utils/Utility';
import { EditVersionSmart } from './EditVersionSmart';
import { Log } from '../utils/Log';
import { ShareAPI } from '../services/API/ShareAPI';

const DashboardPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [cvVersions, setCvVersions] = useState<CVVersion[]>([]);
    const [loading, setLoading] = useState(true);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [selectedSections, setSelectedSections] = useState<string[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedVersion, setSelectedVersion] = useState<CVVersion | null>(null);

    // Share state
    const [shareAnchorEl, setShareAnchorEl] = useState<null | HTMLElement>(null);
    const [shareUrl, setShareUrl] = useState<string>('');
    const [shareVersion, setShareVersion] = useState<CVVersion | null>(null);
    const [copied, setCopied] = useState(false);
    const [snackbar, setSnackbar] = useState(false);

    const [pendingNativeShare, setPendingNativeShare] = useState(false);

    const [sharePopoverOpen, setSharePopoverOpen] = useState(false);

    // QR dialog state
    const [qrDialogOpen, setQrDialogOpen] = useState(false);
    const [qrVersion, setQrVersion] = useState<CVVersion | null>(null);
    const [qrUrl, setQrUrl] = useState('');
    const qrCanvasRef = useRef<HTMLCanvasElement>(null);

    const { t } = useTranslation();

    const availableSections = [
        { id: 'personal',       label: t('dashboard.availableSectionPersonalData') },
        { id: 'education',      label: t('dashboard.availableSectionPersonalEducation') },
        { id: 'experience',     label: t('dashboard.availableSectionExperience') },
        { id: 'skills',         label: t('dashboard.availableSectionSkill') },
        { id: 'certifications', label: t('dashboard.availableSectionCertification') },
        { id: 'projects',       label: t('dashboard.availableSectionProject') },
        { id: 'language',       label: t('dashboard.availableSectionLanguage') },
    ];

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [versions] = await Promise.all([
                CvVersionAPI.Instance<CvVersionAPI>().getCVVersions(),
            ]);
            setCvVersions(versions);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteVersion = async (version: CVVersion) => {
        try {
            await CvVersionAPI.Instance<CvVersionAPI>().deleteCVVersion(version.id);
            loadData();
        } catch (error) {
            console.error('Error deleting CV version:', error);
        }
    };

    const handleGeneratePDF = async (version: CVVersion) => {
        try {
            const blob = await apiService.generatePDF(version.id);
            const url = window.URL.createObjectURL(blob);

            if (/iP(ad|hone|od)/i.test(navigator.userAgent)) {
                // iOS: apri il blob direttamente — non serve il reader
                const opened = window.open(url, '_blank');
                if (!opened) alert(t('dashboard.errorPopUp'));
                return;
            }

            // Desktop / Android: click su link invisibile
            const a = document.createElement('a');
            a.href = url;
            a.download = `${version.versionName}.pdf`;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();

            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }, 100);
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    // ---- Share ----

    const buildShareMessage = (version: CVVersion, url: string) => {
        const name = user?.username ?? '';
        const nameLabel = name ? `di ${name}` : 'il mio CV';
        return {
            short: `Ciao, ti condivido il CV ${nameLabel} su MyCVitae: ${url}`,
            email: {
                subject: encodeURIComponent(
                    name ? `CV di ${name} — ${version.versionName}` : version.versionName
                ),
                body: encodeURIComponent(
                    `Ciao,\n\nti condivido il mio CV tramite MyCVitae.\n\n` +
                    `Puoi visualizzarlo qui: ${url}\n\n` +
                    `Il documento è sempre aggiornato — se dovessi aggiornare le mie informazioni, il link rimarrà lo stesso.\n\n` +
                    `A presto${name ? `,\n${name}` : ''}`
                ),
            },
        };
    };

    const handleShare = async (event: React.MouseEvent<HTMLElement>, version: CVVersion) => {
        const target = event.currentTarget;

        try {
            const result = await ShareAPI.Instance<ShareAPI>().createShare(version.id, {});
            const url = result.shareUrl;
            setShareUrl(url);
            setShareVersion(version);

            const shareData = {
                title: version.versionName,
                text: `CV di ${user?.username ?? ''}`,
                url,
            };

            const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
            const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

            if ((isStandalone || isMobile) && navigator.share && navigator.canShare?.(shareData)) {
                try {
                    await navigator.share(shareData);
                    return; // share nativa completata, non aprire il popover
                } catch (e) {
                    if ((e as DOMException).name === 'AbortError') return; // utente ha chiuso, ok
                    // altri errori → fallback al popover sotto
                }
            }

            // Desktop o fallback: apri il popover
            setShareAnchorEl(target);
            setSharePopoverOpen(true);
        } catch (error) {
            console.error('Error creating share:', error);
        }
    };

    const handleShareClose = () => {
        setSharePopoverOpen(false);
        setShareAnchorEl(null);
    };

    const handleCopy = async () => {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setSnackbar(true);
        setTimeout(() => setCopied(false), 2500);
    };

    // ---- Menu contestuale ----

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, version: CVVersion) => {
        setAnchorEl(event.currentTarget);
        setSelectedVersion(version);
    };

    const handleMenuModify = (version: CVVersion) => {
        Log.writeLogDebug(version);
        setSelectedVersion(version);
        setCreateDialogOpen(true);
    };

    const handleAddBtn = () => {
        setSelectedVersion(null);
        setCreateDialogOpen(true);
    };

    const handleMenuClose = () => setAnchorEl(null);

    const handleCloseForm = () => {
        Log.writeLogDebug('CloseForm');
        setCreateDialogOpen(false);
        setLoading(true);
        setSelectedSections([]);
        loadData().then(() => setLoading(false));
    };

    const handleClickViewCv = async (version: CVVersion) => {
        // Apri la finestra SUBITO, nel contesto sincrono del click
        const newWindow = window.open('about:blank', '_blank');

        try {
            const result = await ShareAPI.Instance<ShareAPI>().createShare(version.id, {});
            const isPWA = window.matchMedia('(display-mode: standalone)').matches;
            const url = isPWA ? `${result.shareUrl}?from=pwa` : result.shareUrl;

            if (newWindow) {
                // Cambia l'URL della finestra già aperta
                newWindow.location.href = url;
            } else {
                // Fallback PWA che ha bloccato anche about:blank
                window.location.href = url;
            }
        } catch (error) {
            newWindow?.close();
            console.error('Error viewing CV:', error);
            alert(t('dashboard.errorPopUp'));
        }
    };

    // ---- QR Code ----

    const handleOpenQR = (version: CVVersion, url: string) => {
        setQrVersion(version);
        setQrUrl(url);
        setQrDialogOpen(true);
        handleShareClose();
    };

    const handleOpenQRFromCard = async (version: CVVersion) => {
        try {
            const result = await ShareAPI.Instance<ShareAPI>().createShare(version.id, {});
            setQrVersion(version);
            setQrUrl(result.shareUrl);
            setQrDialogOpen(true);
        } catch (error) {
            console.error('Error creating share for QR:', error);
        }
    };

    const handleDownloadQR = () => {
        const canvas = qrCanvasRef.current;
        if (!canvas) return;
        const pngUrl = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = pngUrl;
        a.download = `${qrVersion?.versionName ?? 'cv'}-qr.png`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleShareQR = async () => {
        const canvas = qrCanvasRef.current;
        if (!canvas) return;

        const blob = await new Promise<Blob | null>((resolve) =>
            canvas.toBlob(resolve, 'image/png')
        );
        if (!blob) return;

        const file = new File([blob], `${qrVersion?.versionName ?? 'cv'}-qr.png`, { type: 'image/png' });

        if (navigator.canShare?.({ files: [file] })) {
            try {
                await navigator.share({
                    title: qrVersion?.versionName ?? 'CV',
                    files: [file],
                });
            } catch (e) {
                if ((e as DOMException).name !== 'AbortError') throw e;
            }
        } else {
            // Fallback: condivide solo l'URL
            await navigator.share({
                title: qrVersion?.versionName ?? 'CV',
                text: qrUrl,
            });
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Typography>{t('standardUse.loading')}</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" component="h1">
                    {t('dashboard.dashboard')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {t('dashboard.wellCame')}, {user?.username}!
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* CV Versions */}
                <Grid size={12}>
                    <Typography variant="h5" gutterBottom>
                        {t('dashboard.yourCvVersion')}
                    </Typography>
                    <Grid container spacing={2}>
                        {cvVersions.map((version) => (
                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={version.id}>
                                <Card>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box>
                                                <Typography variant="h6" component="h3" gutterBottom>
                                                    {version.versionName}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    {t('dashboard.template')}: {version.templateName}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                    {version.selectedSections.map((section) => (
                                                        <Chip
                                                            key={section}
                                                            label={availableSections.find(s => s.id === section)?.label || section}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenQRFromCard(version)}
                                                    title="QR Code"
                                                >
                                                    <QrCodeIcon />
                                                </IconButton>
                                                <IconButton onClick={(e) => handleMenuClick(e, version)} size="small">
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                    <CardActions>
                                        <Button
                                            size="small"
                                            startIcon={<VisibilityIcon />}
                                            onClick={() => handleClickViewCv(version)}
                                        >
                                            {t('dashboard.viewCv')}
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            onClick={() => handleGeneratePDF(version)}
                                        >
                                            {t('standardUse.pdf')}
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={<ShareIcon />}
                                            onClick={(e) => handleShare(e, version)}
                                        >
                                            {t('standardUse.sharing')}
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                {/* Ad Banner */}
                <Grid size={{ xs: 12 }}>
                    <AdBanner />
                </Grid>
            </Grid>

            {/* Share Popover */}
            {shareVersion && (() => {
                const msg = buildShareMessage(shareVersion, shareUrl);
                return (
                    <Popover
                        open={sharePopoverOpen}
                        anchorEl={shareAnchorEl}
                        onClose={handleShareClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: 3, minWidth: 280, p: 0.75 } } }}
                    >
                        {/* URL + copia */}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            px: 1.5, py: 1, mb: 0.75,
                            bgcolor: 'action.hover', borderRadius: 2,
                        }}>
                            <InputBase
                                value={shareUrl}
                                readOnly
                                sx={{ flex: 1, fontSize: 12, fontFamily: 'monospace', color: 'text.secondary' }}
                            />
                            <Tooltip title={copied ? t('standardUse.copied') : t('standardUse.copy')}>
                                <IconButton size="small" onClick={handleCopy}>
                                    {copied
                                        ? <CheckIcon fontSize="small" color="success" />
                                        : <ContentCopyIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Typography variant="caption" sx={{ px: 1.5, color: 'text.disabled', fontWeight: 500 }}>
                            {t('standardUse.openIn')}
                        </Typography>

                        {/* Email */}
                        <MenuItem
                            component="a"
                            href={`mailto:?subject=${msg.email.subject}&body=${msg.email.body}`}
                            onClick={handleShareClose}
                            sx={{ borderRadius: 2, mt: 0.5 }}
                        >
                            <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
                            <ListItemText
                                primary="Email"
                                secondary={t('dashboard.shareEmailSub')}
                                slotProps={{ secondary: { sx: { fontSize: 11 } } }}
                            />
                        </MenuItem>

                        {/* WhatsApp */}
                        <MenuItem
                            component="a"
                            href={`https://wa.me/?text=${encodeURIComponent(msg.short)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleShareClose}
                            sx={{ borderRadius: 2 }}
                        >
                            <ListItemIcon>
                                <Box
                                    component="img"
                                    src="/icons/whatsapp.svg"
                                    alt="WhatsApp"
                                    sx={{ width: 20, height: 20 }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary="WhatsApp"
                                secondary={t('dashboard.shareWhatsappSub')}
                                slotProps={{ secondary: { sx: { fontSize: 11 } } }}
                            />
                        </MenuItem>

                        {/* LinkedIn */}
                        <MenuItem
                            component="a"
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleShareClose}
                            sx={{ borderRadius: 2 }}
                        >
                            <ListItemIcon>
                                <Box
                                    component="img"
                                    src="/icons/linkedin.svg"
                                    alt="LinkedIn"
                                    sx={{ width: 20, height: 20, borderRadius: 0.5 }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary="LinkedIn"
                                secondary={t('dashboard.shareLinkedinSub')}
                                slotProps={{ secondary: { sx: { fontSize: 11 } } }}
                            />
                        </MenuItem>

                        {/* Telegram */}
                        <MenuItem
                            component="a"
                            href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(msg.short)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleShareClose}
                            sx={{ borderRadius: 2 }}
                        >
                            <ListItemIcon>
                                <Box
                                    component="img"
                                    src="/icons/telegram.svg"
                                    alt="Telegram"
                                    sx={{ width: 20, height: 20 }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary="Telegram"
                                secondary={t('dashboard.shareTelegramSub')}
                                slotProps={{ secondary: { sx: { fontSize: 11 } } }}
                            />
                        </MenuItem>

                        {/* QR Code */}
                        <MenuItem
                            onClick={() => handleOpenQR(shareVersion, shareUrl)}
                            sx={{ borderRadius: 2 }}
                        >
                            <ListItemIcon>
                                <Box
                                    component="img"
                                    src="/icons/qrcode.svg"
                                    alt="QR Code"
                                    sx={{ width: 20, height: 20 }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary="QR Code"
                                secondary={t('dashboard.shareQrSub')}
                                slotProps={{ secondary: { sx: { fontSize: 11 } } }}
                            />
                        </MenuItem>

                        <Divider sx={{ my: 0.75 }} />
                        <Typography variant="caption" sx={{ px: 1.5, pb: 0.75, display: 'block', color: 'text.disabled' }}>
                            {t('dashboard.shareFootnote')}
                        </Typography>
                    </Popover>
                );
            })()}

            {/* QR Code Dialog */}
            <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{t('dashboard.qrCodeTitle')}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
                    <QRCodeCanvas
                        ref={qrCanvasRef}
                        value={qrUrl}
                        size={256}
                        level="H"
                        marginSize={2}
                        imageSettings={{
                            src: '/logo192.png',
                            height: 40,
                            width: 40,
                            excavate: true,
                        }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', wordBreak: 'break-all' }}>
                        {qrUrl}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Button onClick={() => setQrDialogOpen(false)}>
                        {t('standardUse.closeBtn')}
                    </Button>
                    <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownloadQR}>
                        {t('dashboard.qrDownload')}
                    </Button>
                    {typeof navigator.share === 'function' && (
                        <Button variant="contained" color="secondary" startIcon={<ShareIcon />} onClick={handleShareQR}>
                            {t('standardUse.sharing')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Snackbar feedback copia */}
            <Snackbar
                open={snackbar}
                autoHideDuration={2500}
                onClose={() => setSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" variant="filled" onClose={() => setSnackbar(false)} sx={{ fontSize: 13 }}>
                    {t('dashboard.shareLinkCopied')}
                </Alert>
            </Snackbar>

            {/* Floating Action Button */}
            <Fab
                color="primary"
                aria-label="add"
                sx={{ position: 'fixed', bottom: 16, right: 16 }}
                onClick={handleAddBtn}
            >
                <AddIcon />
            </Fab>

            {/* Version Dialog */}
            <EditVersionSmart
                item={Utility.ConvertToType<CVVersion>(selectedVersion)}
                onSave={handleCloseForm}
                onCancel={handleCloseForm}
                open={createDialogOpen}
                onClose={handleCloseForm}
            />

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => {
                    Log.writeLogDebug(selectedVersion);
                    if (selectedVersion) handleMenuModify(selectedVersion);
                    handleMenuClose();
                }}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t('standardUse.update')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => {
                    if (selectedVersion) handleDeleteVersion(selectedVersion);
                    handleMenuClose();
                }}>
                    <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t('standardUse.delete')}</ListItemText>
                </MenuItem>
            </Menu>
        </Container>
    );
};

export default DashboardPage;